import { useEffect, useMemo, useRef, useState } from 'react';
import ThemeProvider from '../../../../../packages/theme/ThemeProvider';
import { BaseWidgetProps, IFootballConfig, LineupStatuses } from '../../../../../packages/types';
import { LineupsWidget, MAX_LINEUPS_WIDTH } from '../Lineups';
import { SingleEventWidget } from '../SingleEvent';
import { StandingsWidget } from '../Standings';
import {
  FOOTBALL_SINGLE_EVENT,
  LINEUPS,
  MATCHES_H2H,
  ODDS,
  STANDINGS,
  TEAM_H2H,
  TIMELINE,
} from '../../../../../packages/constants/widgetIds';
import { getLabelValue, getMatchDateExtended } from '../../../../../packages/utils';
import {
  extractMatchInformation,
  generateCoreConfigInternalProps,
  generateSimpleId,
  generateTabLinksStyles,
} from '../../helpers/matchCenter';
import { MatchesH2HWidget } from '../MatchesH2H';
import useSingleEvent from '../../hooks/useSingleEvent';
import { TimelineWidget } from '../Timeline';
import { GeneralTabInfo } from '../Timeline/EventInfo';
import { MatchInfoItem, SMPOpacitorContainer } from '../../../../../packages/ui';
import useResizeObserver from '../../../../../packages/hooks/useResizeObserver';
import { MAX_MOBILE_WIDTH } from '../../../../../packages/constants/generic';
import { TeamMatchH2hWidget } from '../TeamMatchH2HWidget';
import { FINISHED, LIVE } from '../../../../../packages/constants/statusTypes';
import { OddsWidget } from '../Odds';

export type MatchCenterWidgetProps = BaseWidgetProps<IFootballConfig>;

export const MatchCenterWidget = ({ themes, config, subscribers, sdkOptions, shadow }: MatchCenterWidgetProps) => {
  const commonIntegratedWidgetsProps = {
    themes,
    subscribers,
    sdkOptions,
    getAuthToken: () => '',
    signOut: () => '',
  };
  const [isFirstLoad, setIsFirstLoad] = useState(true); // this is because stitches bug that does not load all styles on first render
  const [selectedTab, setSelectedTab] = useState('');
  const [isMobileSize, setIsMobileSize] = useState(false);

  const tabsContainerRef = useRef<HTMLDivElement | null>(null);
  const tabsRefs = useRef<{ [key: string]: HTMLDivElement | null }>({}); // Individual tab references

  const displayMainEvents = config?.dataMatchCenterMainEventsDisplay;

  // generateSimpleId is used because there is a problem when using cuid.
  // Storybook does not render the widget and its permanently loading.
  // Maybe the problem is because we are using cuid on parent level and in child level.
  const identifierPrefix = useMemo(() => {
    const prefix = '-from-match-widget';
    return {
      [FOOTBALL_SINGLE_EVENT]: `${generateSimpleId()}${prefix}`,
      [LINEUPS]: `${generateSimpleId()}${prefix}`,
      [TIMELINE]: `${generateSimpleId()}${prefix}`,
      [TEAM_H2H]: `${generateSimpleId()}${prefix}`,
      [STANDINGS]: `${generateSimpleId()}${prefix}`,
      [ODDS]: `${generateSimpleId()}${prefix}`,
      [MATCHES_H2H]: `${generateSimpleId()}${prefix}`,
    };
  }, []);

  const measuredRef = useResizeObserver((fieldElement) => {
    setIsMobileSize(fieldElement.offsetWidth < MAX_MOBILE_WIDTH);
  });

  const {
    widgetData: { footballSingleEvent },
  } = useSingleEvent({
    dataWidgetId: config?.dataWidgetId,
    dataMatchId: config?.dataMatchId,
    dataWidgetSport: config?.dataWidgetSport,
    dataSingleEventMainEventsDisplay: displayMainEvents,
    dataDisplayLineupStatus: true,
  });

  const isUpcoming = footballSingleEvent?.status?.type !== LIVE && footballSingleEvent?.status?.type !== FINISHED;
  const isUpcomingOrLive = footballSingleEvent?.status?.type !== FINISHED;

  const generateTabLinksStylesGeneral = (isActive: boolean) => generateTabLinksStyles(isActive, isMobileSize, isUpcoming);

  const determineDefaultTab = () => {
    const lineupStatus = footballSingleEvent?.lineupsStatus;
    const stageType = footballSingleEvent?.stage?.type;

    if (lineupStatus === LineupStatuses.NOT_AVAILABLE) {
      if (stageType === 'LEAGUE' || stageType === 'GROUP') {
        return STANDINGS;
      } else {
        return MATCHES_H2H;
      }
    }

    // Default to 'Information' tab for CONFIRMED or UNCONFIRMED lineup_status
    return LINEUPS;
  };

  const handleTabScroll = (indicator: string) => {
    const tabElement = tabsRefs.current[indicator];
    if (tabElement && tabsContainerRef.current) {
      const tabLeft = tabElement.offsetLeft;
      const tabWidth = tabElement.offsetWidth;
      const containerScrollLeft = tabsContainerRef.current.scrollLeft;
      const containerWidth = tabsContainerRef.current.offsetWidth;

      // Check if the tab is fully visible in the container
      if (tabLeft < containerScrollLeft || tabLeft + tabWidth > containerScrollLeft + containerWidth) {
        tabsContainerRef.current.scrollTo({
          left: tabLeft - containerWidth / 2 + tabWidth / 2,
          behavior: 'smooth',
        });
      }
    }
  };
  const handleTabClick = (tabId: string) => {
    setSelectedTab(tabId);
    handleTabScroll(tabId);
  };

  useEffect(() => {
    if (isFirstLoad) {
      const defaultTab = determineDefaultTab();
      setSelectedTab(defaultTab);
      handleTabScroll(defaultTab);

      const timeout = setTimeout(() => {
        setIsFirstLoad(false);
      }, 2000);

      return () => {
        clearTimeout(timeout);
      };
    }
  }, [isFirstLoad, footballSingleEvent]);

  const statisticConfig = useMemo(() => {
    // this is made only to prevent the TeamH2hWidget re-rendering
    return {
      dataSportEntityOne: {
        id: `${footballSingleEvent?.homeTeam.id}`,
        seasonId: `${footballSingleEvent?.season?.id}`,
      },
      dataSportEntityTwo: {
        id: `${footballSingleEvent?.awayTeam.id}`,
        seasonId: `${footballSingleEvent?.season?.id}`,
      },
    };
  }, [footballSingleEvent?.homeTeam?.id, footballSingleEvent?.awayTeam?.id, footballSingleEvent?.season?.id]);

  const filteredOddsTabBettingIds = useMemo(() => {
    if (config?.dataOddsDisplay) {
      const tabBettingIds = config?.dataOddsTabBettingId
        ?.toString()
        ?.split(',')
        .map((id) => id.trim());
      const bettingIds = config?.dataOddsBettingId
        ?.toString()
        ?.split(',')
        .map((id) => id.trim());

      if (!tabBettingIds || !bettingIds) return config?.dataOddsTabBettingId;

      // Filter to include only IDs not present in dataOddsBettingId
      const uniqueTabBettingIds = tabBettingIds.filter((id) => !bettingIds.includes(id));

      // Return the filtered list as a string
      return uniqueTabBettingIds.join(', ');
    }
  }, [config?.dataOddsTabBettingId, config?.dataOddsBettingId, config?.dataOddsDisplay]);

  if (!footballSingleEvent) {
    return null;
  }

  const matchGeneralInformation = extractMatchInformation(footballSingleEvent, config?.dataLabels, sdkOptions);

  return (
    <ThemeProvider
      slickStyles={true}
      shadow={shadow}
      themes={themes}
      dataTheme={config?.dataTheme}
      isLoading={false}
      css={{
        backgroundColor: 'var(--colors-matchCenterPrimaryBackgroundColor)',
        maxWidth: `${MAX_LINEUPS_WIDTH}px`,
      }}
      removeMaxWidth
    >
      <SMPOpacitorContainer isLoading={isFirstLoad}>
        <div style={{ visibility: isFirstLoad ? 'hidden' : 'visible' }}>
          <SingleEventWidget
            {...commonIntegratedWidgetsProps}
            cuid={identifierPrefix[FOOTBALL_SINGLE_EVENT]}
            config={{
              ...generateCoreConfigInternalProps(config, FOOTBALL_SINGLE_EVENT),
              dataAdditionalInfo: true,
              dataSingleEventMainEventsDisplay: displayMainEvents,
            }}
          />
        </div>
        {/* Tabs */}
        <div
          ref={tabsContainerRef}
          className='custom-tabs'
          style={{
            display: 'flex',
            overflowX: 'auto',
            overflowY: 'hidden',
            scrollbarWidth: 'none',
            WebkitOverflowScrolling: 'touch',
            width: '100%',
            paddingTop: '32px',
            paddingBottom: '32px',
          }}
        >
          <div
            ref={(el) => (tabsRefs.current[LINEUPS] = el)}
            className='custom-tablinks-lineups'
            onClick={() => handleTabClick(LINEUPS)}
            style={{
              ...generateTabLinksStylesGeneral(selectedTab === LINEUPS),
              borderRadius: isMobileSize ? '0px' : '8px 0px 0px 0px',
            }}
          >
            {getLabelValue(config?.dataLabels?.match_information_tab, 'Information')}
          </div>
          <div
            ref={(el) => (tabsRefs.current[TIMELINE] = el)}
            className='custom-tablinks-timeline'
            onClick={() => handleTabClick(TIMELINE)}
            style={{ ...generateTabLinksStylesGeneral(selectedTab === TIMELINE) }}
          >
            {getLabelValue(config?.dataLabels?.match_timeline_tab, 'Timeline')}
          </div>
          {!isUpcoming && (
            <div
              ref={(el) => (tabsRefs.current[TEAM_H2H] = el)}
              className='custom-tablinks-team-h2h'
              onClick={() => handleTabClick(TEAM_H2H)}
              style={generateTabLinksStylesGeneral(selectedTab === TEAM_H2H)}
            >
              {getLabelValue(config?.dataLabels?.match_statistics_tab, 'Statistics')}
            </div>
          )}
          <div
            ref={(el) => (tabsRefs.current[STANDINGS] = el)}
            className='custom-tablinks-standings'
            onClick={() => handleTabClick(STANDINGS)}
            style={generateTabLinksStylesGeneral(selectedTab === STANDINGS)}
          >
            {footballSingleEvent.stage?.type === 'KNOCK_OUT'
              ? getLabelValue(config?.dataLabels?.match_playoff_tab, 'Playoff')
              : getLabelValue(config?.dataLabels?.match_standings_tab, 'Standings')}
          </div>
          {isUpcomingOrLive && config?.dataOddsDisplay && (
            <div
              ref={(el) => (tabsRefs.current[ODDS] = el)}
              className='custom-tablinks-odds'
              onClick={() => handleTabClick(ODDS)}
              style={generateTabLinksStylesGeneral(selectedTab === ODDS)}
            >
              {getLabelValue(config?.dataLabels?.match_odds_tab, 'Odds')}
            </div>
          )}
          <div
            ref={(el) => (tabsRefs.current[MATCHES_H2H] = el)}
            className='custom-tablinks-match-h2h'
            onClick={() => handleTabClick(MATCHES_H2H)}
            style={{
              ...generateTabLinksStylesGeneral(selectedTab === MATCHES_H2H),
              borderRadius: isMobileSize ? '0px' : '0px 8px 0px 0px',
              borderRight: 'none',
            }}
          >
            {getLabelValue(config?.dataLabels?.match_h2h_tab, 'H2H')}
          </div>
        </div>
        {/* Tabs end */}

        <div style={{ display: selectedTab !== LINEUPS || isFirstLoad ? 'none' : 'block' }}>
          <LineupsWidget
            {...commonIntegratedWidgetsProps}
            cuid={identifierPrefix[LINEUPS]}
            config={{ ...generateCoreConfigInternalProps(config, LINEUPS) }}
          />
        </div>
        <div
          style={{
            display: selectedTab !== TIMELINE || isFirstLoad ? 'none' : 'block',
            maxWidth: '600px',
            margin: '0 auto',
          }}
        >
          <TimelineWidget
            {...commonIntegratedWidgetsProps}
            cuid={identifierPrefix[TIMELINE]}
            config={{
              ...generateCoreConfigInternalProps(config, TIMELINE),
              widgetStartTime: footballSingleEvent?.kickoffTime
                ? getMatchDateExtended(footballSingleEvent.kickoffTime, sdkOptions, 'HH:mm z')
                : undefined,
            }}
          />
        </div>
        <div style={{ display: selectedTab !== TEAM_H2H || isFirstLoad ? 'none' : 'block' }}>
          <TeamMatchH2hWidget
            {...commonIntegratedWidgetsProps}
            cuid={identifierPrefix[TEAM_H2H]}
            config={{
              ...generateCoreConfigInternalProps(config, TEAM_H2H),
              ...statisticConfig,
            }}
          />
        </div>
        <div style={{ display: selectedTab !== MATCHES_H2H || isFirstLoad ? 'none' : 'block' }}>
          <MatchesH2HWidget
            {...commonIntegratedWidgetsProps}
            cuid={identifierPrefix[MATCHES_H2H]}
            config={{
              ...generateCoreConfigInternalProps(config, MATCHES_H2H),
              dataSportEntityOne: `${footballSingleEvent?.homeTeam.id}`,
              dataSportEntityTwo: `${footballSingleEvent?.awayTeam.id}`,
              dataLimit: config?.dataLimit || '5',
            }}
          />
        </div>
        <div style={{ display: selectedTab !== STANDINGS || isFirstLoad ? 'none' : 'block' }}>
          <StandingsWidget
            {...commonIntegratedWidgetsProps}
            cuid={identifierPrefix[STANDINGS]}
            config={{
              ...generateCoreConfigInternalProps(config, STANDINGS),
              dataCompetition: footballSingleEvent?.season?.competition?.id,
              dataStage: footballSingleEvent?.stage?.id,
              dataSeason: footballSingleEvent?.season?.id,
              dataGroups: footballSingleEvent?.group?.id ? [+footballSingleEvent?.group?.id] : undefined,
              dataImageDisplay: true,
              dataLimit: undefined,
              dataHighlightEntities: [`${footballSingleEvent?.homeTeam.id}`, `${footballSingleEvent?.awayTeam.id}`],
            }}
          />
        </div>
        {isUpcomingOrLive && config?.dataOddsDisplay && (
          <div style={{ display: selectedTab !== ODDS || isFirstLoad ? 'none' : 'block' }}>
            <OddsWidget
              {...commonIntegratedWidgetsProps}
              cuid={identifierPrefix[ODDS]}
              config={{
                ...generateCoreConfigInternalProps(config, ODDS),
                dataOddsMarket: config?.dataOddsMarket,
                dataOddsMarketValueType: config?.dataOddsMarketValueType,
                dataOddsPreEventOnly: config?.dataOddsPreEventOnly,
                dataOddsBettingId: filteredOddsTabBettingIds,
                dataMatchId: config?.dataMatchId,
                dataBettingLogoClickAction: config?.dataBettingLogoClickAction,
                dataBettingLogoOverlap: config?.dataBettingLogoOverlap,
              }}
            />
          </div>
        )}
        <div className='match-information-section' ref={measuredRef}>
          <GeneralTabInfo style={{ marginBottom: '32px', marginTop: '32px', fontSize: '14px' }}>
            {getLabelValue(config?.dataLabels?.match_information, 'MATCH INFORMATION')}
          </GeneralTabInfo>
          <div
            className='match-information-data'
            style={{
              width: isMobileSize ? '100%' : '80%',
              margin: '0 auto',
              display: 'flex',
              justifyContent: 'space-evenly',
              flexWrap: 'wrap',
              gap: isMobileSize ? '0' : '32px',
            }}
          >
            {Object.values(matchGeneralInformation).map((item) => (
              <MatchInfoItem key={item.label} {...item} isMobile={isMobileSize} />
            ))}
          </div>
        </div>
      </SMPOpacitorContainer>
    </ThemeProvider>
  );
};
